/** PaintingGalleryScreen — 完成した名画(構図マスク勝利ステージ)のギャラリーモーダル。
 *  Reference: GDD §0.2 USP 2「名画が完成する」、CLAUDE.md §3.4 Discovery Reward
 *
 *  Callers:
 *    - web/src/main.ts: new PaintingGalleryScreen({ ... })
 *    - web/src/ui/screens/HomeScreen.ts: onOpenGallery callback → galleryScreen.open()
 */

import { StageLoader } from '../../game/StageLoader';

export interface PaintingGalleryScreenOptions {
  /** どの mask_cells ステージが完成済みかを返す callback */
  readonly getClearedStages: () => ReadonlySet<string>;
  readonly onClose: () => void;
}

interface PaintingEntry {
  readonly stageId: string;
  readonly title: string;
  readonly description: string;
  readonly maskCells: readonly { readonly x: number; readonly y: number }[];
  readonly cleared: boolean;
}

export class PaintingGalleryScreen {
  private readonly overlay: HTMLDivElement;
  private readonly options: PaintingGalleryScreenOptions;
  private isOpen = false;

  constructor(options: PaintingGalleryScreenOptions) {
    this.options = options;
    this.overlay = document.createElement('div');
    this.overlay.className = 'painting-gallery-overlay';
    this.overlay.setAttribute('aria-hidden', 'true');
    this.overlay.setAttribute('role', 'dialog');
    this.overlay.setAttribute('aria-modal', 'true');
    this.overlay.setAttribute('aria-label', '完成名画ギャラリー');
    document.body.appendChild(this.overlay);
  }

  open(): void {
    if (this.isOpen) return;
    this.isOpen = true;
    this.render();
    this.overlay.classList.add('is-open');
    this.overlay.setAttribute('aria-hidden', 'false');
  }

  close(): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.overlay.classList.remove('is-open');
    this.overlay.setAttribute('aria-hidden', 'true');
  }

  private render(): void {
    const cleared = this.options.getClearedStages();
    // StageLoader から composition_mask 勝利のステージを抽出
    const paintings: PaintingEntry[] = [];
    for (const stage of StageLoader.listStages()) {
      if (stage.victory_conditions.primary.type !== 'composition_mask') continue;
      paintings.push({
        stageId: stage.stage_id,
        title: stage.metadata.title,
        description: stage.metadata.description,
        maskCells: stage.victory_conditions.primary.mask_cells ?? [],
        cleared: cleared.has(stage.stage_id),
      });
    }

    const cards = paintings.map((p) => this.buildCardHTML(p)).join('');
    const clearedCount = paintings.filter((p) => p.cleared).length;
    const totalCount = paintings.length;

    this.overlay.innerHTML = `
      <div class="painting-gallery-card">
        <div class="painting-gallery-header">
          <div class="painting-gallery-title">美術館</div>
          <div class="painting-gallery-count">${clearedCount} / ${totalCount}</div>
          <button class="painting-gallery-close" type="button" data-action="gallery-close" aria-label="閉じる">✕</button>
        </div>
        <div class="painting-gallery-body">
          ${cards.length > 0 ? cards : '<div class="painting-gallery-empty">まだ完成した名画はありません</div>'}
        </div>
      </div>
    `;

    // 閉じる
    this.overlay.querySelectorAll<HTMLElement>('[data-action="gallery-close"]')
      .forEach((el) => el.addEventListener('click', () => { this.close(); this.options.onClose(); }));
    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) { this.close(); this.options.onClose(); }
    });
  }

  /** カード:SVG で名画(mask cells のシルエット)を描画 */
  private buildCardHTML(p: PaintingEntry): string {
    const cellSize = 6;
    // mask cells の bbox を取って収まるサイズで描画
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const c of p.maskCells) {
      if (c.x < minX) minX = c.x;
      if (c.y < minY) minY = c.y;
      if (c.x > maxX) maxX = c.x;
      if (c.y > maxY) maxY = c.y;
    }
    if (!Number.isFinite(minX)) { minX = 0; minY = 0; maxX = 30; maxY = 30; }
    const w = (maxX - minX + 1) * cellSize;
    const h = (maxY - minY + 1) * cellSize;
    const rects = p.maskCells.map((c) => {
      const x = (c.x - minX) * cellSize;
      const y = (c.y - minY) * cellSize;
      return `<rect x="${x}" y="${y}" width="${cellSize}" height="${cellSize}" />`;
    }).join('');
    const fill = p.cleared ? '#ff8a40' : '#cccccc';
    const opacity = p.cleared ? '0.9' : '0.25';

    return `
      <div class="painting-gallery-tile ${p.cleared ? 'is-cleared' : 'is-locked'}">
        <div class="painting-gallery-frame">
          <svg viewBox="0 0 ${w} ${h}" preserveAspectRatio="xMidYMid meet" width="100%" height="120">
            <g fill="${fill}" fill-opacity="${opacity}">${rects}</g>
          </svg>
        </div>
        <div class="painting-gallery-tile-title">${p.title}</div>
        <div class="painting-gallery-tile-desc">${p.cleared ? p.description : '未完成 — クリアで解放'}</div>
        <div class="painting-gallery-tile-status">${p.cleared ? '🖼️ 完成' : '🔒'}</div>
      </div>
    `;
  }
}
